import React from 'react';
import PropTypes from 'prop-types';
import { some } from 'lodash';
import Link from 'components/link';
import Icon from 'components/icon';
import ViewPreferencesMenu from 'components/view/preferences-menu';
import { objectToArray, locationWithQuery } from 'utils';
import classNames from 'classnames';
import './tabs.scss';

export default class Tabs extends React.PureComponent {
  static contextTypes = {
    clientTabs: PropTypes.array,
    swellTabs: PropTypes.array,
    cleanSaveTab: PropTypes.func,
  };

  static propTypes = {
    items: PropTypes.array.isRequired,
    active: PropTypes.string,
    className: PropTypes.string,
    location: PropTypes.object,
    router: PropTypes.object,
    onClick: PropTypes.func,
    withPreferences: PropTypes.bool,
    overflow: PropTypes.bool,
    overflowProps: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...this.getTabState(props),
    };

    this.buttonChildren = <Icon fa="ellipsis-h" />;
  }

  componentDidMount() {
    window.addEventListener('resize', this.onContainerResize);
    this.onContainerResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onContainerResize);
  }

  componentDidUpdate(prevProps) {
    const { items, withPreferences } = this.props;
    if (
      prevProps.items !== items ||
      prevProps.active !== this.props.active ||
      prevProps.withPreferences !== withPreferences
    ) {
      this.setState({
        ...this.getTabState(this.props),
      });
    }
  }

  getTabState(props) {
    const allItems = objectToArray(props.items);
    const visibleItems = allItems.filter((tab) => !tab.hidden);

    let numVisible = this.getNumVisibleItems();

    let items = visibleItems.slice(0, numVisible);

    let activeOverflowItem;
    if (numVisible < visibleItems.length) {
      activeOverflowItem = visibleItems
        .slice(numVisible)
        .find((tab) => this.isActive(tab, tab.id));

      if (activeOverflowItem) {
        numVisible = this.getNumVisibleItems(activeOverflowItem);
        items = visibleItems.slice(0, numVisible);
        items.push(activeOverflowItem);
      }
    }

    const moreItems = [
      ...visibleItems
        .slice(numVisible)
        .filter((item) => item !== activeOverflowItem),
      ...allItems.filter((tab) => tab.hidden),
    ];

    return {
      items,
      moreItems,
      allItems,
    };
  }

  getNumVisibleItems(activeOverflowItem = undefined) {
    const { overflow = false } = this.props;

    // Calculate how many tabs can fit in the container without overflowing
    const element = this.refs.tabContainer;
    if (!element || !overflow) {
      return 100;
    }

    let numVisible = 0;

    const containerWidth = element.clientWidth;
    const itemElements = Array.from(this.refs.tabsHidden.children);

    let tabsWidthTotal = 200; // starting aprox. width of the "more items"
    if (activeOverflowItem) {
      const activeOverflowChild = itemElements.find(
        (child) => child.dataset.tabName === activeOverflowItem.label,
      );
      tabsWidthTotal += activeOverflowChild?.offsetWidth || 0;
    }

    for (const tab of itemElements) {
      tabsWidthTotal += tab.offsetWidth;
      if (containerWidth > tabsWidthTotal) {
        numVisible += 1;
      }
    }

    return numVisible;
  }

  onContainerResize = () => {
    const { overflow = false } = this.props;
    const { allItems, isContentOverflowing: prevContentOverflowing } =
      this.state;

    if (!overflow) {
      return;
    }

    const isContentOverflowing = this.getNumVisibleItems() < allItems.length;

    if (isContentOverflowing !== prevContentOverflowing) {
      this.setState({
        ...this.getTabState(this.props),
        isContentOverflowing,
      });
    }
  };

  onClick = (event, tab) => {
    const { onClick } = this.props;
    if (onClick) {
      onClick(tab);
      event.preventDefault();
    }
  };

  isActive = (tab, key) => {
    const { items, active, location } = this.props;
    if (tab.link) {
      if (location.pathname.endsWith(tab.link)) {
        return true;
      }
      if (key === 'default') {
        const anyActive = some(items, (tab) =>
          location.pathname.endsWith(tab.link),
        );
        if (!anyActive) {
          return true;
        }
      }
      return false;
    }
    return active === key || (!active && key === 'default');
  };

  onClickTabItem = (item) => {
    const { onClick, router, location } = this.props;
    if (onClick) {
      onClick(item.id);
    }
    if (router && location) {
      router.push(
        locationWithQuery(location, {
          tab: item.id === 'default' ? undefined : item.id,
        }),
      );
    }
  };

  renderPreferencesMenu() {
    const { items, withPreferences } = this.props;
    const { clientTabs, swellTabs, cleanSaveTab } = this.context;

    if (!withPreferences) {
      return null;
    }

    return (
      <div className="tabs-preferences">
        <ViewPreferencesMenu
          sideOffset={12}
          alignOffset={-2}
          buttonChildren={this.buttonChildren}
          preferenceKey="tabs"
          items={items}
          clientItems={clientTabs}
          swellItems={swellTabs}
          cleanSaveItem={cleanSaveTab}
          onClickItem={this.onClickTabItem}
        />
      </div>
    );
  }

  renderTab(tab, key) {
    const { location } = this.props;
    const id = tab.id || key;

    return (
      <li
        key={id}
        className={this.isActive(tab, id) ? 'active' : ''}
        data-tab-name={tab.label}
      >
        <Link
          to={
            tab.link ||
            (location &&
              locationWithQuery(location, {
                tab: id === 'default' ? undefined : id,
              }))
          }
          onClick={(event) =>
            this.onClick(event, id === 'default' ? undefined : id)
          }
        >
          {tab.label}
        </Link>
      </li>
    );
  }

  renderMoreTabs() {
    const { overflow = false, overflowProps } = this.props;
    const { allItems, moreItems } = this.state;
    const { cleanSaveTab } = this.context;

    if (!overflow) {
      return null;
    }

    return (
      <>
        {moreItems.length > 0 && (
          <li className="tabs-more">
            <ViewPreferencesMenu
              listOnly={true}
              {...overflowProps}
              preferenceKey="tabs"
              buttonChildren={<>{moreItems.length} more...</>}
              items={allItems}
              filter={(item) => moreItems.find((mi) => mi.id === item.id)}
              cleanSaveItem={cleanSaveTab}
              onClickItem={this.onClickTabItem}
            />
          </li>
        )}
      </>
    );
  }

  render() {
    const { className } = this.props;
    const { items, allItems } = this.state;

    return (
      <div className="tabs-container" ref="tabContainer">
        {/* Render all tabs for measuring visible size of items */}
        <ul className="tabs invisible" ref="tabsHidden">
          {allItems.map((tab, key) => {
            if (!tab) return null;
            return this.renderTab(tab, key);
          })}
        </ul>
        <ul className={classNames('tabs', className)} ref="tabs">
          {items.map((tab, key) => {
            if (!tab || tab.hidden) return null;
            return this.renderTab(tab, key);
          })}
          {this.renderMoreTabs()}
        </ul>
        {this.renderPreferencesMenu()}
      </div>
    );
  }
}
